import React from "react";
import ShallowRenderer from "react-shallow-renderer";
import ReferenceStroke, {
  ReferenceFill,
  ReferenceTrace,
} from "../models/ReferenceStroke";

export const SIZE = 64;

export default function extractStrokesFromSVG(
  SVGComponent: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >
): ReferenceStroke[] {
  const renderer = new ShallowRenderer();
  renderer.render(<SVGComponent />);
  const element = renderer.getRenderOutput();

  if (element === null) {
    throw new InterpretationError(
      `the svg component returned null unexpectedly`
    );
  }
  if (element.type !== "svg") {
    throw new InterpretationError(
      `expected root element to be <svg>, but found <${element.type}>`
    );
  }
  if (
    element.props.width.toString() !== `${SIZE}` ||
    element.props.height.toString() !== `${SIZE}`
  ) {
    throw new InterpretationError(
      `expected width and height to be ${SIZE}, but found ${element.props.width} and ${element.props.height}`
    );
  }
  if (element.props.viewBox !== `0 0 ${SIZE} ${SIZE}`) {
    throw new InterpretationError(
      `expected viewBox to be "0 0 ${SIZE} ${SIZE}", but found ${element.props.viewBox}`
    );
  }

  const rootChildren = React.Children.toArray(element.props.children);
  if (
    rootChildren.length !== 1 ||
    !React.isValidElement(rootChildren[0]) ||
    rootChildren[0].type !== "g"
  ) {
    throw new InterpretationError(
      "expected <svg> to have exactly one top-level <g> child"
    );
  }
  const strokeNodes = React.Children.toArray(rootChildren[0].props.children);

  const strokes = strokeNodes.flatMap((stroke) => {
    if (!React.isValidElement(stroke)) {
      throw new InterpretationError(
        "unexpected non-element child of top-level group"
      );
    }
    if (
      stroke.type === "rect" &&
      stroke.props.fill === "white" &&
      stroke.props.width === SIZE &&
      stroke.props.height === SIZE
    ) {
      return []; // ignore background
    }
    if (stroke.type !== "g") {
      throw new InterpretationError(
        `expected top-level group to contain <g> elements for each stroke, but found <${stroke.type}>`
      );
    }

    const id = stroke.props.id;
    if (typeof id !== "string") {
      throw new InterpretationError("expected stroke <g> to have a string id");
    }
    const idMatch = id.match(/^Stroke (\d)+$/);
    if (idMatch === null) {
      throw new InterpretationError(
        `expected stroke <g> id to be "Stroke 1" and so on, but found "${id}"`,
        id === "Character Grid"
      );
    }
    const number = parseInt(idMatch[1], 10);

    let fill: ReferenceFill | undefined;
    let trace: ReferenceTrace | undefined;
    const paths = React.Children.toArray(stroke.props.children);
    for (const path of paths) {
      if (!React.isValidElement(path) || path.type !== "path") {
        throw new InterpretationError(
          `expected stroke <g> to contain only <path> elements`
        );
      }
      const pathId = path.props.id;
      if (typeof pathId !== "string") {
        throw new InterpretationError(
          "expected stroke path to have a string id"
        );
      }
      if (/^Fill(_\d+)?$/.test(pathId)) {
        fill = { path: path.props.d };
      } else if (/^Trace(_\d+)?$/.test(pathId)) {
        trace = { path: path.props.d, strokeWidth: path.props.strokeWidth };
      } else {
        throw new InterpretationError(
          `expected stroke path id to start with "Fill" or "Trace", but found "${pathId}"`
        );
      }
    }
    if (fill === undefined) {
      throw new InterpretationError(
        'expected stroke group to contain a path with an id starting with "Fill"'
      );
    }
    if (trace === undefined) {
      throw new InterpretationError(
        'expected stroke group to contain a path with an id starting with "Trace"'
      );
    }
    return { number, fill, trace };
  });
  strokes.reverse();
  if (
    strokes[0].number !== 1 ||
    !strokes.every(
      (stroke, index) =>
        index === 0 || stroke.number === strokes[index - 1].number + 1
    )
  ) {
    throw new InterpretationError(
      `expected stroke numbers to be 1, 2, 3, ... in order, but found ${strokes
        .map((s) => s.number)
        .join(", ")}`
    );
  }
  return strokes.map(({ number, ...stroke }) => stroke);
}

export class InterpretationError extends Error {
  constructor(message: string, public isNotDoneYet: boolean = false) {
    super(message);
    this.name = "InterpretationError";
  }
}
